import { FAILURE_STATUSES, TABLE_STATUS_LABEL } from "./extractionStatus";
import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

import type { ExtractionSummary } from "@/api/config-server-stages";
import { Button } from "@/components/ui/button";
import { StageNavButtons } from "@/components/agents/configuration-server/stages/stageNav";
import { stageStep } from "@/components/agents/configuration-server/stages/stageOrder";
import { Skeleton } from "@/components/ui/skeleton";

const STATUS_COLOR: Record<string, string> = {
  data: "var(--exed-success)",
  empty: "#8fd3a8",
  not_authorized: "var(--exed-danger)",
  not_found: "var(--exed-warn)",
  structural_error: "#dc2626",
  error: "#7b1d21",
};

const STATUS_ORDER = ["data", "empty", ...FAILURE_STATUSES];
const PANEL_CLASS = "rounded-lg border border-[var(--exed-card-border)] bg-[var(--exed-card-bg)]";
const DUAL_BAR_GAP = "gap-0.5";

function BarList({
  label,
  rows,
  numberFormat,
  labelWidth,
  valueWidth,
}: {
  label: string;
  rows: { key: string; name: string; value: number; secondary?: number }[];
  numberFormat: Intl.NumberFormat;
  /** Larguras da grade no desktop, como no design (níveis: 170px · 48px; áreas: 140px · 72px). */
  labelWidth: string;
  valueWidth: string;
}) {
  const { t } = useTranslation();
  const sortedRows = [...rows].sort((left, right) => {
    const leftTotal = left.value + (left.secondary ?? 0);
    const rightTotal = right.value + (right.secondary ?? 0);
    return rightTotal - leftTotal;
  });
  const max = Math.max(1, ...sortedRows.map((row) => row.value + (row.secondary ?? 0)));

  return (
    <ul aria-label={label} className="m-0 flex list-none flex-col gap-3 p-0">
      {sortedRows.length === 0 && (
        <li className="text-sm text-[var(--exed-ink-500)]">{t("No data")}</li>
      )}
      {sortedRows.map((row) => (
        <li
          key={row.key}
          className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 sm:[grid-template-columns:var(--label-w)_minmax(0,1fr)_var(--value-w)]"
          style={{ "--label-w": labelWidth, "--value-w": valueWidth } as React.CSSProperties}
        >
          <span className="min-w-0 break-words text-[13px] text-[var(--exed-ink-900)]">{row.name}</span>
          <span
            aria-hidden="true"
            className={cn(
              "col-span-2 row-start-2 flex h-1.5 overflow-hidden rounded-full bg-[#eef1f5] sm:col-span-1 sm:col-start-2 sm:row-start-1",
              row.secondary != null && DUAL_BAR_GAP,
            )}
          >
            <span className="h-full bg-[var(--exed-navy)]" style={{ width: `${(row.value / max) * 100}%` }} />
            {row.secondary != null && (
              <span className="h-full bg-[var(--exed-gold)]" style={{ width: `${(row.secondary / max) * 100}%` }} />
            )}
          </span>
          <span className="col-start-2 row-start-1 text-right text-[13px] font-semibold tabular-nums sm:col-start-3">
            {row.secondary != null ? (
              <>
                <span className="text-[var(--exed-ink-900)]">{numberFormat.format(row.value)}</span>
                <span className="font-normal text-[var(--exed-ink-500)]"> · </span>
                <span className="text-[#8a6100]">{numberFormat.format(row.secondary)}</span>
              </>
            ) : (
              <span className="text-[var(--exed-ink-900)]">{numberFormat.format(row.value)}</span>
            )}
          </span>
        </li>
      ))}
    </ul>
  );
}

function Kpi({
  testId,
  label,
  value,
  numberFormat,
  ready = true,
}: {
  testId: string;
  label: string;
  value: number;
  numberFormat: Intl.NumberFormat;
  ready?: boolean;
}) {
  return (
    <div data-testid={testId} aria-busy={!ready || undefined} className={`${PANEL_CLASS} flex flex-col gap-1 px-5 py-4`}>
      <p className="text-xs font-medium text-[var(--exed-ink-500)]">{label}</p>
      {ready ? <p className="text-[28px] font-bold leading-[1.15] tracking-[-0.02em] tabular-nums text-[var(--exed-ink-900)]">
        {numberFormat.format(value)}
      </p> : <Skeleton aria-hidden="true" className="h-9 w-24" />}
    </div>
  );
}

export function ExtractionDashboard({
  summary,
  onShowFailures,
  readyDomains,
}: {
  summary: ExtractionSummary;
  onShowFailures?: () => void;
  readyDomains?: readonly ("org" | "sscui" | "fiori")[];
}) {
  const { t, i18n } = useTranslation();
  const numberFormat = useMemo(
    () => new Intl.NumberFormat(i18n.language || "en-US"),
    [i18n.language],
  );
  const notAuthorized = summary.tables_by_status.not_authorized ?? 0;
  const failures = FAILURE_STATUSES.reduce(
    (sum, status) => sum + (summary.tables_by_status[status] ?? 0),
    0,
  );
  const total = Math.max(1, summary.tables_total);
  const hasOtherFailures = failures > notAuthorized;
  const ready = new Set(readyDomains ?? ["org", "sscui", "fiori"]);
  const allReady = ready.has("org") && ready.has("sscui") && ready.has("fiori");

  return (
    <div className="space-y-4">
      <section className={`${PANEL_CLASS} flex flex-col gap-3.5 px-6 pb-6 pt-5`}>
        {allReady ? <>
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div className="flex flex-col gap-0.5">
            <h2 className="m-0 text-xs font-medium text-[var(--exed-ink-500)]">{t("Tables read")}</h2>
            <p data-testid="tables-read" className="m-0 flex items-baseline gap-1.5">
              <span className="text-[32px] font-bold leading-[1.1] tracking-[-0.02em] tabular-nums text-[var(--exed-ink-900)]">
                {numberFormat.format(summary.tables_read)}
              </span>{" "}
              <span className="text-base text-[var(--exed-ink-500)]">
                {t("of {{total}}", { total: numberFormat.format(summary.tables_total) })}
              </span>
            </p>
          </div>
          <span className="text-xs text-[var(--exed-ink-500)]">
            {t("{{records}} records", { records: numberFormat.format(summary.records_total) })}
          </span>
        </div>
        <div aria-hidden="true" className="flex h-2 gap-0.5 overflow-hidden rounded-full bg-[var(--exed-card-border)]">
          {STATUS_ORDER.map((status) => {
            const count = summary.tables_by_status[status] ?? 0;
            return count > 0 ? (
              <span
                key={status}
                className="h-full"
                style={{ width: `${(count / total) * 100}%`, background: STATUS_COLOR[status] }}
              />
            ) : null;
          })}
        </div>
        <ul aria-label={t("Tables read")} className="m-0 flex list-none flex-wrap gap-x-4 gap-y-1 p-0 text-xs text-[var(--exed-ink-500)]">
          {STATUS_ORDER.filter((status) => (summary.tables_by_status[status] ?? 0) > 0).map((status) => (
            <li key={status} className="inline-flex items-center gap-1.5">
              <span
                aria-hidden="true"
                className="h-2 w-2 shrink-0 rounded-full"
                style={{ background: STATUS_COLOR[status] }}
              />
              {t(TABLE_STATUS_LABEL[status] ?? status)}:{" "}
              <strong className="font-semibold text-[var(--exed-ink-900)]">{numberFormat.format(summary.tables_by_status[status])}</strong>
            </li>
          ))}
        </ul>
        {failures > 0 && (
          <div
            role="alert"
            className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-[rgba(220,38,38,0.25)] bg-[rgba(220,38,38,0.06)] py-2.5 pl-3.5 pr-2.5"
          >
            <span className="inline-flex min-w-0 items-center gap-2.5 text-sm font-medium text-[#991b1b]">
              <AlertTriangle aria-hidden="true" className="h-4 w-4 shrink-0 text-[#dc2626]" />
              <span>
                {hasOtherFailures
                  ? t("{{count}} tables could not be read", { count: failures })
                  : t("{{count}} tables without authorization", { count: notAuthorized })}
                {hasOtherFailures && notAuthorized > 0 && (
                  <span className="block">
                    {t("{{count}} tables without authorization", { count: notAuthorized })}
                  </span>
                )}
              </span>
            </span>
            <Button
              variant="outline"
              size="sm"
              className="h-8 border-[rgba(220,38,38,0.25)] text-[13px] text-[#991b1b] hover:bg-[rgba(220,38,38,0.06)] hover:text-[#991b1b]"
              disabled={!onShowFailures}
              onClick={onShowFailures}
            >
              {t("See list")}
            </Button>
          </div>
        )}
        </> : <>
          <h2 className="m-0 text-xs font-medium text-[var(--exed-ink-500)]">{t("Tables read")}</h2>
          <Skeleton aria-hidden="true" data-testid="tables-loading" className="h-36 w-full" />
        </>}
      </section>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4">
        <Kpi
          testId="kpi-org"
          label={t("Organizational structure units")}
          value={summary.org_units_total}
          numberFormat={numberFormat}
          ready={ready.has("org")}
        />
        <Kpi
          testId="kpi-sscui"
          label={t("SSCUI configurations")}
          value={summary.sscui_total}
          numberFormat={numberFormat}
          ready={ready.has("sscui")}
        />
        <Kpi
          testId="kpi-fiori"
          label={t("App Fiori configurations")}
          value={summary.fiori_total}
          numberFormat={numberFormat}
          ready={ready.has("fiori")}
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(380px,1fr))] items-start gap-4">
        <section className={`${PANEL_CLASS} flex flex-col gap-3.5 px-6 py-5`}>
          <h2 className="m-0 text-base font-semibold text-[var(--exed-ink-900)]">
            {t("Organizational structure by level")}
          </h2>
          {ready.has("org") ? <BarList
            labelWidth="170px"
            valueWidth="48px"
            label={t("Organizational structure by level")}
            numberFormat={numberFormat}
            rows={summary.org_units_by_level.map((level) => ({
              key: level.level_code,
              name: level.level_name,
              value: level.count,
            }))}
          /> : <Skeleton aria-hidden="true" className="h-48 w-full" />}
        </section>
        <section className={`${PANEL_CLASS} flex flex-col gap-3.5 px-6 py-5`}>
          <div className="flex flex-col gap-2">
            <h2 className="m-0 text-base font-semibold text-[var(--exed-ink-900)]">
              {t("SSCUI and App Fiori by area")}
            </h2>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--exed-ink-500)]">
              <span data-testid="area-legend-sscui" className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--exed-navy)]" />
                {t("SSCUI configurations")}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span aria-hidden="true" className="h-2 w-2 rounded-full bg-[var(--exed-gold)]" />
                {t("App Fiori configurations")}
              </span>
            </div>
          </div>
          {ready.has("sscui") && ready.has("fiori") ? <BarList
            labelWidth="140px"
            valueWidth="72px"
            label={t("SSCUI and App Fiori by area")}
            numberFormat={numberFormat}
            rows={[...summary.by_area].sort((a, b) => Number(a.application_area == null) - Number(b.application_area == null)).map((area) => ({
              key: area.application_area == null ? "missing-area" : `area:${area.application_area}`,
              name: area.application_area ?? t("Others"),
              value: area.sscui,
              secondary: area.fiori,
            }))}
          /> : <Skeleton aria-hidden="true" className="h-48 w-full" />}
        </section>
      </div>
    </div>
  );
}

/**
 * Barra de decisão: fica no pé da janela, na largura inteira, e é o último
 * filho da coluna da página (por isso não mora dentro do dashboard).
 * `bottom-[-1.5rem]` compensa o `p-6` do `<main>` do AppLayout: o sticky respeita
 * o padding do contêiner de rolagem, e com `bottom-0` a barra parava 24px acima.
 */
export function DecisionBar({
  pending,
  centralHref,
  managementAvailable = true,
}: {
  pending: number;
  centralHref: string;
  managementAvailable?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <div
      data-testid="decision-bar"
      className="sticky bottom-[-1.5rem] z-10 mt-auto border-t border-[var(--exed-card-border)] bg-white px-6 py-3"
    >
      <div className="mx-auto flex max-w-[1232px] flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3 text-[13px] text-[var(--exed-ink-500)]">
          {managementAvailable && (
            <>
              <span>{t("{{count}} items ready for decision", { count: pending })}</span>
              <span aria-hidden="true" className="text-[var(--exed-ink-400)]">·</span>
            </>
          )}
          <span>{t("Step {{step}} of 3", { step: stageStep("extraction") })}</span>
        </div>
        <StageNavButtons stage="extraction" centralHref={centralHref} nextDisabled={!managementAvailable} />
      </div>
    </div>
  );
}
